import { Dialog, DialogActions, DialogContent, Typography, GridList, GridListTile, GridListTileBar, makeStyles } from '@material-ui/core'
import { Spinner, Button } from 'components'
import clone from 'lodash/clone'
import { MediaLibraryFile } from 'app/entities/types'
import React from 'react'
import { Color } from 'theme/style'
import { MediaLibraryFolderValue } from 'app/values'
import clsx from 'clsx'
import {
  uploadMediaLibraryFile, UploadMediaLibraryFileRequestParams,
  getMediaLibraryFolder, GetMediaLibraryFolderRequestParams, GetMediaLibraryFolderResponse
} from 'app/api';
import { toast, convertFileToBase64, getBaseName, getFileName } from 'app/utils';
import { useSelector } from 'react-redux'
import { RootState } from 'app/session/store'
import { useLocalization } from 'components/methods'
import { getMediaLibraryFileName, getMediaLibraryFileFormat, userCan, getMediaLibraryFolderFromValue } from 'app/entities/methods'
import { UserPermissionValue } from 'app/values'
import AddIcon from '@material-ui/icons/Add'

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialog: {
    width: 'calc(100% - 60px)',
    maxWidth: 'calc(100% - 60px)',
    height: 'calc(100% - 60px)',
    maxHeight: 'calc(100% - 60px)',
  },
  dialogHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '30px',
  },
  titleContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  col_thumbnail: {
    flex: '0 0 100px',
    maxWidth: 100,
  },
  row: {
    height: 80
  },
  thumbnail: {
    width: 70,
    height: 70,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover'
  },
  fileContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  uploadMessage: {
    marginRight: theme.spacing(2),
  },
  gridList: {
    width: '100%',
    height: '100%',
  },
  gridItem: {
    border: '4px solid transparent',
    cursor: 'pointer'
  },
  gridItemSelected: {
    borderColor: Color.Primary
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
}))


interface FilePickerProps {
  open: boolean
  items?: MediaLibraryFile[]
  selectedItem?: MediaLibraryFile | null
  title?: string
  description?: string
  mediaLibraryFolder?: MediaLibraryFolderValue
  enableUpload?: boolean
  onClose: () => void
  onSelectCallback: (selectedItem: MediaLibraryFile | null) => void
  onConfirmCallback: (selectedItem: MediaLibraryFile | null) => void
}

const FilePicker = ({ open, items, selectedItem, title, description, mediaLibraryFolder, enableUpload, onClose, onSelectCallback, onConfirmCallback }: FilePickerProps) => {
  const { t } = useLocalization()
  const classes = useStyles()
  const session = useSelector((state: RootState) => state.session)

  const [isLoading, setIsLoading] = React.useState(true)
  const [isFetching] = React.useState(false)

  const [files, setFiles] = React.useState<MediaLibraryFile[] | null>(null)
  const [selection, setSelection] = React.useState<MediaLibraryFile | null>(null)

  const inputFile = React.useRef<HTMLInputElement>(null)
  const [fileToUpload, setFileToUpload] = React.useState<File | null>(null)
  const [isUploading, setIsUploading] = React.useState(false)

  if (title == null) title = t('component.FilePicker.title')
  if (description == null) description = t('component.FilePicker.descrption')
  if (enableUpload == null) enableUpload = false



  //INITIALIZATION

  React.useEffect(() => {
    if (open === false) return

    const initialItems = clone(items)
    setFileToUpload(null)

    if (initialItems == null) {
      fetchItems()
    }
    else {
      setFiles(validateItems(initialItems))
      setSelection(selectedItem ?? null)
    }
  }, [open])

  React.useEffect(() => {
    const initialized = files != null
      && isFetching === false
    setIsLoading(!initialized)
  }, [open, files, isFetching])



  //ITEMS

  function fetchItems() {
    if (isFetching === true) return

    if (mediaLibraryFolder == null) {
      console.error("Media Library folder not specified: unable to fetch items.")
      setFiles([])
    }

    fetchItems()

    function fetchItems() {
      const encode = (): GetMediaLibraryFolderRequestParams => {
        const folder = getMediaLibraryFolderFromValue(session, mediaLibraryFolder!)
        return {
          id: folder?.id ?? 0
        }
      }

      const decode = (data: GetMediaLibraryFolderResponse): MediaLibraryFile[] => {
        let object: MediaLibraryFile[] = []
        data.folder.files?.forEach((f, i) => {
          object.push({
            id: f.id,
            name: f.filename,
            format: f.type,
            path: f.basepath + '/' + f.filename,
          })
        })

        //Removes the dev avatar if the user is not a dev.
        if (mediaLibraryFolder === MediaLibraryFolderValue.Avatar) {
          if (userCan(session.user, UserPermissionValue.BackofficeAccess) === false) {
            object = object.filter(o => o.name.includes('avatar0') === false);
          }
        }

        return object
      }

      getMediaLibraryFolder(encode(), {
        response(data) {
          const filesList = decode(data)
          setFiles(validateItems(filesList, true))
          setSelection(selectedItem ?? null)
        },
        error(error, message) {
          toast.error(message)
        }
      })
    }
  }

  function validateItems(list?: MediaLibraryFile[], formatFilename?: boolean): MediaLibraryFile[] {
    if (list == null) return []

    list.forEach(i => {
      validateItem(i)
    })

    return clone(list)

    function validateItem(item: MediaLibraryFile) {
      if (item.format == null) item.format = getMediaLibraryFileFormat(item);

      const basename = getBaseName(item.name)
      let filename = getFileName(basename)
      if (formatFilename === true) filename = getMediaLibraryFileName(item)
      item.name = filename
    }
  }



  //ACTIONS

  function selectItem(item: MediaLibraryFile) {
    let itemToSelect: MediaLibraryFile | null = item
    if (itemToSelect.id === selection?.id) itemToSelect = null
    setSelection(itemToSelect)
    onSelectCallback(itemToSelect)
  }

  function confirmSelection() {
    onConfirmCallback(selection)
    onClose()
  }



  //UPLOAD

  //Shows the browser dialog to select a file.
  function promptFileToUpload() {
    if (enableUpload === false) return
    if (inputFile == null) return
    inputFile.current?.click()
  }

  //Triggered when the file is selected from the picker.
  function selectedFileToUpload(event: any) {
    if (enableUpload === false) return

    const target = event.target as HTMLInputElement
    const files = target.files
    if (files == null) return
    const value = files[0]
    setFileToUpload(value)
  }

  //Automatically uploads the file once it has been selected.
  React.useEffect(() => {
    if (enableUpload === false) return
    if (fileToUpload == null) return
    if (isUploading === true) return

    uploadFile()
  }, [fileToUpload])

  //Uploads the file to the server.
  async function uploadFile() {
    if (enableUpload === false) return
    if (fileToUpload == null) return

    //Converts the file to base64.
    const base64File = await convertFileToBase64(fileToUpload as any)
    uploadFileToMediaLibrary(fileToUpload, base64File)

    //Perform the actual file upload.
    function uploadFileToMediaLibrary(originalFile: File, fileData: any) {
      setIsUploading(true)
      const encode = (): UploadMediaLibraryFileRequestParams => {
        const folder = getMediaLibraryFolderFromValue(session, mediaLibraryFolder!)
        return {
          filename: getFileName(originalFile.name) ?? '',
          fileData: fileData,
          folderId: folder?.id ?? 0,
        }
      }

      uploadMediaLibraryFile(encode(), {
        response(data) {
          toast.success(t('alert.mediafile.uploaded'))
          fetchItems()
          setIsUploading(false)
          setFileToUpload(null)
        },
        error(error, message) {
          toast.error(message)
          setIsUploading(false)
          setFileToUpload(null)
        }
      })
    }
  }



  //RENDER

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialog }}>
      <div className={classes.dialogHeader}>
        <div className={classes.titleContainer}>
          <Typography component="h2" variant="h2">{title}</Typography>
          <Typography component="p" variant="body1">{description}</Typography>
        </div>

        {enableUpload === true && (
          <div className={classes.fileContainer}>
            <input type="file" ref={inputFile} style={{ visibility: 'hidden', display: 'none' }} onChange={selectedFileToUpload} />
            {fileToUpload != null && (
              <>
                {/* <Typography className={classes.uploadMessage}>Selezionato: {fileToUpload.name}</Typography> */}
                {isUploading === false && (<Button category="secondary" endIcon={<Spinner type={'small'} />} onClick={uploadFile}>{t('component.FilePicker.upload-file')}</Button>)}
                {isUploading === true && (<Button category="secondary" endIcon={<Spinner type={'small'} />}>{t('component.FilePicker.uploading-file')}</Button>)}
              </>
            )}
            {fileToUpload == null && (
              <Button category={'secondary'} endIcon={<AddIcon />} onClick={promptFileToUpload}>{t('component.FilePicker.action.add-new')}</Button>
            )}
          </div>
        )}
      </div>

      <DialogContent>
        {isLoading === true && (
          <div className={classes.loadingContainer}>
            <Spinner />
          </div>
        )}

        {(isLoading === false && files != null && files.length === 0) && (
          <div className={classes.loadingContainer}>
            <Typography className={classes.uploadMessage}>No results</Typography>
          </div>
        )}

        {(isLoading === false && files != null && files.length > 0) && (
          <GridList className={classes.gridList} cols={8}>
            {files?.map((item, i) => (
              <GridListTile className={item.id === selection?.id ? clsx(classes.gridItem, classes.gridItemSelected) : clsx(classes.gridItem)} key={i} cols={1} onClick={e => selectItem(item)}>
                <img src={item.path} alt={item.name} />
                <GridListTileBar title={item.name} subtitle={item.format} />
              </GridListTile>
            ))}
          </GridList>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose}>{t('component.FilePicker.close')}</Button>
        <Button category={'confirm'} prominent={true} onClick={confirmSelection}>{t('component.FilePicker.confirm')}</Button>
      </DialogActions>
    </Dialog >
  )
}

export default FilePicker
